import { Component, OnInit, OnDestroy, TemplateRef, ViewChild, inject } from '@angular/core';
import { AuthService, BlockAbstractComponent, CacheService, ParserService } from '@gallaghercp/core-frontend';
import { HttpClient } from '@angular/common/http';
import { CampusType } from '@core-app/client/types/campus.type';
import { CampusService } from '@core-app/services/campus.service';
import { getUnixTime } from 'date-fns';
import { map, switchMap, startWith, filter } from 'rxjs/operators';
import { CalendarEvent, CalendarView } from '@gallaghercp/core-calendar';
import { Observable, Subscription } from 'rxjs';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';

interface CalendarAppEvent extends CalendarEvent {
  title: string;
  description?: string;
  type?: string;
  date?: string;
  month?: string;
}

@Component({
  selector: 'core-callout-external',
  templateUrl: './block-event-tiles.component.html',
  standalone: false,
})
export class BlockEventTilesComponent extends BlockAbstractComponent implements OnInit, OnDestroy {
  protected override parser: ParserService;
  protected override auth: AuthService;
  protected http = inject(HttpClient);
  private cache = inject(CacheService);
  private campus = inject(CampusService);
  private modalService = inject(BsModalService);
  private translate = inject(TranslateService);

  @ViewChild('eventModal') eventModal?: TemplateRef<any>;
  currentLocale = 'en-US';
  calendarType = 'public-holidays'; // @TODO: make block variable
  view: CalendarView = CalendarView.Month;
  events$!: Observable<CalendarAppEvent[]>;
  modalRef!: BsModalRef;
  modalData?: {
    action: string;
    event: CalendarAppEvent;
  };
  private subscription: Subscription = new Subscription();

  constructor() {
    const parser = inject(ParserService);
    const auth = inject(AuthService);

    super(parser, auth);

    this.parser = parser;
    this.auth = auth;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.events$ = this.campus.selectedCampus$.pipe(
      filter(campusId => campusId !== null), // Only proceed when we have a campus
      startWith(this.campus.selectedCampus), // Start with current value
      switchMap(() => this.fetchEventsYear()),
    );
  }

  override ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  handleEvent(action: string, event: CalendarAppEvent): void {
    this.modalData = { event, action };
    this.openEventModal();
  }

  openEventModal(): void {
    this.modalRef = this.modalService.show(this.eventModal!);
  }

  closeEventModal(): void {
    this.modalRef?.hide();
  }

  getCalendar(): any[] {
    const enabledCampuses = this.campus.getEnabledCampuses();
    const currentCampusId = this.campus.selectedCampus;
    // Add safety checks
    if (!enabledCampuses || enabledCampuses.length === 0 || !currentCampusId) {
      console.warn('No enabled campuses or current campus ID available');
      return [];
    }
    // Convert to number to ensure proper comparison
    const currentCampusIdNumber = Number(currentCampusId);
    // Find the campus by ID/zone instead of using array index
    const currentCampus = enabledCampuses.find(
      campus => Number(campus.id) === currentCampusIdNumber || Number(campus.zone) === currentCampusIdNumber,
    );

    if (!currentCampus) {
      console.warn(`Campus with ID ${currentCampusIdNumber} not found in enabled campuses:`, enabledCampuses);
      return [];
    }

    return [currentCampus]
      .filter(
        (campus: any) => (campus.calendars ?? []).filter((calendar: any) => calendar.type === this.calendarType).length,
      )
      .map((campus: CampusType) => {
        const matchingCalendar = (
          (campus.calendars ?? []).filter((calendar: any) => calendar.type === this.calendarType) as any
        )[0];

        return { calendar: matchingCalendar?.calendar };
      });
  }

  fetchEventsYear(): Observable<CalendarAppEvent[]> {
    const calendar = this.getCalendar();

    // If no calendar is available, return empty array
    if (calendar.length === 0) {
      console.warn('No calendar available for current campus');
      return new Observable(subscriber => {
        subscriber.next([]);
        subscriber.complete();
      });
    }

    const now = new Date();
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(now.getFullYear() + 1);

    const startDate = getUnixTime(this.toGMT(now));
    const endDate = getUnixTime(this.toGMT(oneYearFromNow));

    return this.cache
      .getListThroughCache('calendar/events', {
        calendars: JSON.stringify(calendar),
        startDate: startDate,
        endDate: endDate,
      })
      .pipe(
        map((results: any[]) => {
          // Map the raw results into CalendarAppEvent format
          const mappedResults = results.map((event: any) => {
            return {
              start: new Date(event.start), // Convert start to Date
              end: new Date(event.end), // Convert end to Date
              title: event.summary, // Parsed event title
              description: event.description ?? 'No description available', // Fallback for null descriptions
              color: {
                primary: '#2196F3', // Default event color
                secondary: '#BBDEFB',
              },
              allDay: event.start.length < 11, // Check if this is an all-day event
              type: event.summary.includes('Holiday') ? 'holiday' : 'event', // Classify event type
            };
          });

          // Sort events by `start` date in ascending order
          mappedResults.sort((a, b) => a.start.getTime() - b.start.getTime());

          return mappedResults;
        }),
        // Slice the sorted array to return only the first 3 earliest events
        map((sortedResults: CalendarAppEvent[]) => sortedResults.slice(0, 3)),
      );
  }

  // Add a method to get localized month
  getLocalizedMonth(date: Date): string {
    type SupportedLanguage = 'en' | 'es';

    const monthNames: Record<SupportedLanguage, string[]> = {
      en: ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'],
      es: ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'],
    };

    const currentLang = this.translate.currentLang || 'en';
    const monthIndex = date.getMonth();

    // Type-safe check
    const lang: SupportedLanguage = currentLang === 'es' ? 'es' : 'en';

    return monthNames[lang][monthIndex];
  }

  private toGMT(date: Date): Date {
    const dateCopy = new Date(date.getTime());
    const offset = dateCopy.getTimezoneOffset();
    dateCopy.setMinutes(dateCopy.getMinutes() - offset);
    return dateCopy;
  }
}
